import React, { useState } from "react";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createTRPCClient, httpLink, TRPCClientError } from "@trpc/client";
import { TRPCProvider } from "./trpc";
import type { AppRouter } from "@backend/routers";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "@shared/components/toaster";

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Clerk Publishable Key");
}

const REQUEST_TIMEOUT_MS = 15_000;
const MAX_RETRIES = 3;
const NON_RETRYABLE_STATUSES = [401, 403];

function shouldRetry(failureCount: number, error: Error): boolean {
  if (error instanceof TRPCClientError) {
    const status = error.data?.httpStatus;
    if (NON_RETRYABLE_STATUSES.includes(status)) return false;
  }
  return failureCount < MAX_RETRIES;
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60 * 1000,
        retry: shouldRetry,
      },
    },
  });
}

let browserQueryClient: QueryClient | undefined = undefined;

function getQueryClient() {
  if (typeof window === "undefined") {
    return makeQueryClient();
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient();
    return browserQueryClient;
  }
}

function TrpcProvider({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();
  const queryClient = getQueryClient();

  const authenticatedFetch = async (
    url: URL | RequestInfo,
    options?: RequestInit,
  ): Promise<Response> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    if (options?.signal) {
      options.signal.addEventListener("abort", () => controller.abort());
    }

    const token = await getToken();
    const headers: Record<string, string> = {
      ...(options?.headers as Record<string, string>),
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  };

  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        httpLink({
          url: `${BACKEND_URL}/trpc`,
          fetch: authenticatedFetch,
        }),
      ],
    })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    </QueryClientProvider>
  );
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY} afterSignOutUrl="/">
      <TrpcProvider>
        <BrowserRouter>
          <Toaster richColors />
          {children}
        </BrowserRouter>
      </TrpcProvider>
    </ClerkProvider>
  );
}
